import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { CalendarOff, Plus, Loader2, Check, X, Trash2, Paperclip, Download, Upload } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { EmployeesTabs } from "../components/employees/EmployeesTabs";
import { getAbsences, createAbsence, updateAbsence, deleteAbsence, getEmployees, getAbsenceAttachments, uploadAbsenceAttachment, downloadAbsenceAttachment, deleteAbsenceAttachment } from "../services/api";

export const ABSENCE_TYPES = [
  { value: "cp", label: "Congés payés", color: "#0ea5e9" },
  { value: "maladie", label: "Maladie", color: "#f43f5e" },
  { value: "rtt", label: "RTT", color: "#8b5cf6" },
  { value: "sans_solde", label: "Sans solde", color: "#64748b" },
  { value: "injustifiee", label: "Absence injustifiée", color: "#dc2626" },
  { value: "formation", label: "Formation", color: "#f59e0b" },
  { value: "autre", label: "Autre", color: "#94a3b8" },
];

const STATUS_CONFIG = {
  pending: { label: "En attente", className: "bg-amber-100 text-amber-700" },
  approved: { label: "Approuvée", className: "bg-green-100 text-green-700" },
  rejected: { label: "Refusée", className: "bg-red-100 text-red-600" },
};

const fmtDate = (d) => new Date(d + "T00:00:00").toLocaleDateString("fr-FR");
const nbDays = (a) => Math.round((new Date(a.end_date) - new Date(a.start_date)) / 86400000) + 1;

export default function AbsencesPage() {
  const [absences, setAbsences] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterEmp, setFilterEmp] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editAbsence, setEditAbsence] = useState(null);
  const [form, setForm] = useState({ employee_id: "", absence_type: "cp", start_date: "", end_date: "", note: "", status: "pending" });
  const [saving, setSaving] = useState(false);
  const [attachAbsence, setAttachAbsence] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [attachLoading, setAttachLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus !== "all") params.status = filterStatus;
      if (filterEmp !== "all") params.employee_id = filterEmp;
      const [absRes, empRes] = await Promise.all([getAbsences(params), getEmployees()]);
      setAbsences(absRes.data);
      setEmployees(empRes.data.filter((e) => e.is_active !== false));
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterEmp]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditAbsence(null);
    setForm({ employee_id: employees[0]?.id || "", absence_type: "cp", start_date: "", end_date: "", note: "", status: "pending" });
    setDialogOpen(true);
  };

  const openEdit = (a) => {
    setEditAbsence(a);
    setForm({ employee_id: a.employee_id, absence_type: a.absence_type, start_date: a.start_date, end_date: a.end_date, note: a.note || "", status: a.status });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.employee_id) { toast.error("Sélectionnez un employé"); return; }
    if (!form.start_date || !form.end_date) { toast.error("Dates requises"); return; }
    if (form.end_date < form.start_date) { toast.error("La date de fin doit être après le début"); return; }
    try {
      setSaving(true);
      if (editAbsence) {
        await updateAbsence(editAbsence.id, form);
        toast.success("Absence mise à jour");
      } else {
        await createAbsence(form);
        toast.success("Absence enregistrée");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (a, status) => {
    try {
      await updateAbsence(a.id, { status });
      toast.success(status === "approved" ? "Absence approuvée" : "Absence refusée");
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Supprimer cette absence (${a.employee_name}, ${a.type_label}) ?`)) return;
    try {
      await deleteAbsence(a.id);
      toast.success("Absence supprimée");
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  const typeInfo = (t) => ABSENCE_TYPES.find((x) => x.value === t) || ABSENCE_TYPES[6];

  const openAttachments = async (a) => {
    setAttachAbsence(a);
    setAttachments([]);
    setAttachLoading(true);
    try {
      const res = await getAbsenceAttachments(a.id);
      setAttachments(res.data || []);
    } catch {
      toast.error("Erreur de chargement des justificatifs");
    } finally {
      setAttachLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !attachAbsence) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (10 Mo max)");
      return;
    }
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append("file", file);
      await uploadAbsenceAttachment(attachAbsence.id, fd);
      toast.success("Justificatif ajouté");
      const res = await getAbsenceAttachments(attachAbsence.id);
      setAttachments(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (f) => {
    try {
      const res = await downloadAbsenceAttachment(f.id);
      const url = URL.createObjectURL(new Blob([res.data], { type: f.content_type || "application/octet-stream" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = f.filename || "justificatif";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error("Impossible de télécharger");
    }
  };

  const handleDeleteAttachment = async (f) => {
    if (!window.confirm(`Supprimer le justificatif « ${f.filename} » ?`)) return;
    try {
      await deleteAbsenceAttachment(f.id);
      toast.success("Justificatif supprimé");
      setAttachments((prev) => prev.filter((x) => x.id !== f.id));
    } catch {
      toast.error("Erreur");
    }
  };

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto" data-testid="absences-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <CalendarOff size={24} className="text-[#26788A]" /> Congés & Absences
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Les absences approuvées apparaissent automatiquement sur le planning</p>
        </div>
        <Button className="bg-[#26788A] hover:bg-[#1D5E6C] text-white" onClick={openCreate} data-testid="new-absence-btn">
          <Plus size={15} className="mr-1.5" /> Nouvelle absence
        </Button>
      </div>

      <EmployeesTabs />

      <div className="flex flex-wrap gap-2 mb-4">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-44 h-9 text-sm bg-white" data-testid="absence-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="pending">En attente</SelectItem>
            <SelectItem value="approved">Approuvées</SelectItem>
            <SelectItem value="rejected">Refusées</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterEmp} onValueChange={setFilterEmp}>
          <SelectTrigger className="w-52 h-9 text-sm bg-white" data-testid="absence-emp-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les employés</SelectItem>
            {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={28} className="animate-spin text-slate-300" /></div>
      ) : absences.length === 0 ? (
        <div className="text-center py-16 bg-white border border-dashed border-slate-200 rounded-xl">
          <CalendarOff size={32} className="mx-auto text-slate-200 mb-2" />
          <p className="text-slate-500">Aucune absence enregistrée</p>
        </div>
      ) : (
        <div className="space-y-2">
          {absences.map((a) => {
            const t = typeInfo(a.absence_type);
            const st = STATUS_CONFIG[a.status] || STATUS_CONFIG.pending;
            return (
              <div key={a.id} className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-center gap-3" data-testid={`absence-row-${a.id}`}>
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: a.employee_color }}></span>
                <div className="min-w-[150px]">
                  <p className="text-sm font-semibold text-slate-900">{a.employee_name}</p>
                  <p className="text-xs text-slate-400">{fmtDate(a.start_date)} → {fmtDate(a.end_date)} · {nbDays(a)} jour{nbDays(a) > 1 ? "s" : ""}</p>
                </div>
                <span className="text-xs font-medium px-2.5 py-1 rounded-full text-white" style={{ backgroundColor: t.color }}>{t.label}</span>
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${st.className}`} data-testid={`absence-status-${a.id}`}>{st.label}</span>
                {a.note && <span className="text-xs text-slate-400 italic flex-1 min-w-[120px] truncate">{a.note}</span>}
                <div className="flex items-center gap-1 ml-auto">
                  <Button size="sm" variant="outline" className="h-8 text-slate-500" onClick={() => openAttachments(a)} data-testid={`attachments-btn-${a.id}`}>
                    <Paperclip size={13} className="mr-1" /> Justificatifs
                  </Button>
                  {a.status === "pending" && (
                    <>
                      <Button size="sm" variant="outline" className="text-green-600 border-green-200 hover:bg-green-50 h-8" onClick={() => setStatus(a, "approved")} data-testid={`approve-absence-${a.id}`}>
                        <Check size={13} className="mr-1" /> Approuver
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-500 border-red-200 hover:bg-red-50 h-8" onClick={() => setStatus(a, "rejected")} data-testid={`reject-absence-${a.id}`}>
                        <X size={13} className="mr-1" /> Refuser
                      </Button>
                    </>
                  )}
                  <Button size="sm" variant="ghost" className="text-slate-400 hover:text-slate-700 h-8" onClick={() => openEdit(a)}>Modifier</Button>
                  <Button size="sm" variant="ghost" className="text-slate-300 hover:text-red-500 h-8 px-2" onClick={() => remove(a)} data-testid={`delete-absence-${a.id}`}>
                    <Trash2 size={13} />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={(v) => !v && setDialogOpen(false)}>
        <DialogContent className="max-w-md" data-testid="absence-dialog">
          <DialogHeader>
            <DialogTitle>{editAbsence ? "Modifier l'absence" : "Nouvelle absence"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="mb-1 block text-xs">Employé</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger data-testid="absence-employee-select"><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1 block text-xs">Type d'absence</Label>
              <Select value={form.absence_type} onValueChange={(v) => setForm({ ...form, absence_type: v })}>
                <SelectTrigger data-testid="absence-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ABSENCE_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: t.color }}></span>{t.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="mb-1 block text-xs">Du</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value, end_date: form.end_date || e.target.value })} data-testid="absence-start-input" />
              </div>
              <div>
                <Label className="mb-1 block text-xs">Au (inclus)</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="absence-end-input" />
              </div>
            </div>
            <div>
              <Label className="mb-1 block text-xs">Statut</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="absence-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="approved">Approuvée</SelectItem>
                  <SelectItem value="rejected">Refusée</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-400 mt-1">Seules les absences approuvées apparaissent sur le planning.</p>
            </div>
            <div>
              <Label className="mb-1 block text-xs">Note (optionnel)</Label>
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Ex : demande par téléphone" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button className="bg-[#26788A] hover:bg-[#1D5E6C] text-white" onClick={handleSave} disabled={saving} data-testid="save-absence-btn">
              {saving && <Loader2 size={14} className="animate-spin mr-1" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Dialog justificatifs */}
      <Dialog open={!!attachAbsence} onOpenChange={(v) => !v && setAttachAbsence(null)}>
        <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="attachments-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Paperclip size={17} className="text-[#26788A]" />
              Justificatifs — {attachAbsence?.employee_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-400">
              {attachAbsence && `${fmtDate(attachAbsence.start_date)} → ${fmtDate(attachAbsence.end_date)} · ${attachAbsence.type_label}`}
              <span className="block mt-0.5">Visible uniquement par vous et l'administrateur.</span>
            </p>
            {attachLoading ? (
              <div className="flex justify-center py-6"><Loader2 size={20} className="animate-spin text-slate-300" /></div>
            ) : attachments.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-5 border border-dashed border-slate-200 rounded-lg">Aucun justificatif pour cette absence</p>
            ) : (
              <div className="space-y-1.5">
                {attachments.map((f) => (
                  <div key={f.id} className="flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2" data-testid={`attachment-${f.id}`}>
                    <Paperclip size={13} className="text-slate-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-800 truncate">{f.filename}</p>
                      <p className="text-[10px] text-slate-400">{(f.size / 1024).toFixed(0)} Ko · ajouté par {f.uploaded_by_name} le {new Date(f.uploaded_at).toLocaleDateString("fr-FR")}</p>
                    </div>
                    <button className="text-slate-400 hover:text-[#26788A] p-1" onClick={() => handleDownload(f)} data-testid={`download-attachment-${f.id}`}>
                      <Download size={15} />
                    </button>
                    <button className="text-slate-300 hover:text-red-500 p-1" onClick={() => handleDeleteAttachment(f)} data-testid={`delete-attachment-${f.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <label className={`flex items-center justify-center gap-2 border-2 border-dashed border-slate-200 hover:border-[#26788A] rounded-lg py-3 cursor-pointer text-sm text-slate-500 hover:text-[#26788A] transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
              {uploading ? "Envoi en cours…" : "Ajouter un justificatif (PDF, image… 10 Mo max)"}
              <input type="file" className="hidden" onChange={handleUpload} accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.doc,.docx" data-testid="attachment-file-input" />
            </label>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
